package org.maplibre.compose.demoapp

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.LocalIndication
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.requiredSize
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.onClick
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import kotlin.math.roundToInt
import kotlin.math.sqrt
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.vectorResource
import org.maplibre.compose.camera.CameraAnimation
import org.maplibre.compose.camera.CameraFit
import org.maplibre.compose.demoapp.generated.Res
import org.maplibre.compose.demoapp.generated.brightness_auto_24px
import org.maplibre.compose.demoapp.generated.dark_mode_24px
import org.maplibre.compose.demoapp.generated.filter_center_focus_24px
import org.maplibre.compose.demoapp.generated.light_mode_24px
import org.maplibre.compose.demoapp.generated.location_disabled_24px
import org.maplibre.compose.demoapp.generated.location_searching_24px
import org.maplibre.compose.demoapp.generated.my_location_24px
import org.maplibre.compose.demoapp.generated.my_location_fill_24px
import org.maplibre.compose.demoapp.generated.navigation_24px
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.compose.map.LocalMapState
import org.maplibre.compose.map.MapEvent
import org.maplibre.compose.map.MapState
import org.maplibre.compose.map.MaplibreMap
import org.maplibre.compose.map.StyleLoadState
import org.maplibre.compose.material3.DisappearingCompassButton as MaterialDisappearingCompassButton
import org.maplibre.compose.material3.DisappearingScaleBar as MaterialDisappearingScaleBar
import org.maplibre.compose.material3.ExpandingAttributionButton as MaterialExpandingAttributionButton
import org.maplibre.compose.material3.PointerPinButton as MaterialPointerPinButton
import org.maplibre.compose.material3.ZoomButtons as MaterialZoomButtons
import org.maplibre.compose.overlay.CompassButtonStyle
import org.maplibre.compose.overlay.CompassDefaults
import org.maplibre.compose.overlay.DisappearingCompassButton
import org.maplibre.compose.overlay.DisappearingScaleBar
import org.maplibre.compose.overlay.ExpandingAttributionButton
import org.maplibre.compose.overlay.GeographicLayout
import org.maplibre.compose.overlay.MapOverlay
import org.maplibre.compose.overlay.MaplibreLogo
import org.maplibre.compose.overlay.PointerPinButton
import org.maplibre.compose.overlay.ZoomButtons
import org.maplibre.compose.overlay.ZoomButtonsDefaults
import org.maplibre.compose.util.DpPadding
import org.maplibre.spatialk.geojson.Position

/** The camera flight to a newly selected demo. */

/** Padding between fitted bounds and the edge of the map viewport. */
val DemoBoundsPadding = DpPadding(left = 48.dp, top = 48.dp, right = 48.dp, bottom = 48.dp)

internal suspend fun MapState.flyTo(destination: DemoDestination, animation: CameraAnimation) {
  when (destination) {
    is DemoDestination.ExactCamera ->
      animateCamera(update = destination.position.toCameraUpdate(), animation = animation)
    is DemoDestination.FitBounds ->
      animateCameraToBounds(
        boundingBox = destination.bounds,
        fit = CameraFit(fitPadding = DemoBoundsPadding),
        animation = animation,
      )
    DemoDestination.None -> Unit
  }
}

private val DemoControlSize = 48.dp

/** Compass enter/exit: fade plus a height change so zoom and theme slide instead of popping. */
private val DemoCompassEnter = fadeIn() + expandVertically()

private val DemoCompassExit = fadeOut() + shrinkVertically()

/** Shared controls whose placement is chosen by the active demo. */
class DemoMapControls(
  val scale: @Composable () -> Unit,
  val buttons: @Composable () -> Unit,
  val attribution: @Composable () -> Unit,
)

/** [controlsModifier] lets a shell route D-pad focus through the button stack. */
internal fun demoMapControls(
  settings: DemoSettings,
  location: DemoLocationUi,
  controlsModifier: Modifier = Modifier,
): DemoMapControls =
  DemoMapControls(
    scale = {
      val mapState = checkNotNull(LocalMapState.current)
      val material3 = settings.useMaterial3Controls
      val metersPerDp = { mapState.viewport?.metersPerDpAtCenter ?: 0.0 }
      val zoom = { mapState.cameraPosition.zoom }
      if (material3) {
        MaterialDisappearingScaleBar(metersPerDp = metersPerDp, zoom = zoom)
      } else {
        DisappearingScaleBar(metersPerDp = metersPerDp, zoom = zoom)
      }
    },
    buttons = {
      val material3 = settings.useMaterial3Controls
      Column(
        modifier = controlsModifier.verticalScroll(rememberScrollState()),
        horizontalAlignment = Alignment.End,
      ) {
        val compassSpacing = Modifier.padding(bottom = MapOverlay.Spacing)
        if (material3) {
          MaterialDisappearingCompassButton(
            contentModifier = compassSpacing,
            enterTransition = DemoCompassEnter,
            exitTransition = DemoCompassExit,
          )
        } else {
          DisappearingCompassButton(
            contentModifier = compassSpacing,
            enterTransition = DemoCompassEnter,
            exitTransition = DemoCompassExit,
          )
        }
        Column(
          verticalArrangement = Arrangement.spacedBy(MapOverlay.Spacing),
          horizontalAlignment = Alignment.End,
        ) {
          if (settings.showZoomButtons) {
            if (material3) MaterialZoomButtons() else ZoomButtons()
          }
          DemoFollowButton(settings, location)
          DemoThemeToggleButton(settings)
        }
      }
    },
    attribution = { DemoMapAttribution(settings.useMaterial3Controls) },
  )

@Composable
private fun DemoFollowButton(settings: DemoSettings, location: DemoLocationUi) {
  val (style, contentColor) = demoControlColors(settings.useMaterial3Controls)
  val visual = location.followVisual()
  val activeColor =
    if (settings.useMaterial3Controls) MaterialTheme.colorScheme.primary else Color(0xFF1565C0)
  val disabledColor =
    if (settings.useMaterial3Controls) MaterialTheme.colorScheme.onSurfaceVariant
    else contentColor.copy(alpha = 0.45f)
  val look =
    when (visual) {
      DemoFollowVisual.Idle ->
        FollowButtonLook(
          Res.drawable.my_location_24px,
          contentColor,
          "Follow your location",
          "Start following",
        )
      DemoFollowVisual.Searching ->
        FollowButtonLook(
          Res.drawable.location_searching_24px,
          activeColor,
          "Finding your location",
          if (location.followMode == DemoFollowMode.Heading) "Stop following" else "Follow heading",
        )
      DemoFollowVisual.Following ->
        FollowButtonLook(
          Res.drawable.my_location_fill_24px,
          activeColor,
          "Following your location",
          "Follow heading",
        )
      DemoFollowVisual.Heading ->
        FollowButtonLook(
          Res.drawable.navigation_24px,
          activeColor,
          "Following your heading",
          "Stop following",
        )
      DemoFollowVisual.Disabled ->
        FollowButtonLook(
          Res.drawable.location_disabled_24px,
          disabledColor,
          "Location is unavailable",
          "Try following",
        )
    }
  DemoControlButton(
    onClick = { location.cycleFollow() },
    style = style,
    contentDescription = look.contentDescription,
    onClickLabel = look.onClickLabel,
  ) {
    Icon(imageVector = vectorResource(look.icon), contentDescription = null, tint = look.tint)
  }
}

private data class FollowButtonLook(
  val icon: DrawableResource,
  val tint: Color,
  val contentDescription: String,
  val onClickLabel: String,
)

@Composable
private fun DemoThemeToggleButton(settings: DemoSettings) {
  val mode = settings.mapStyleMode
  val (style, contentColor) = demoControlColors(settings.useMaterial3Controls)
  DemoControlButton(
    onClick = { settings.mapStyleMode = mode.next },
    style = style,
    contentDescription = "Map style: ${mode.displayName}",
    onClickLabel = "Switch to ${mode.next.displayName}",
  ) {
    Icon(
      imageVector =
        vectorResource(
          when (mode) {
            MapStyleMode.System -> Res.drawable.brightness_auto_24px
            MapStyleMode.Light -> Res.drawable.light_mode_24px
            MapStyleMode.Dark -> Res.drawable.dark_mode_24px
          },
        ),
      contentDescription = null,
      tint = contentColor,
    )
  }
}

@Composable
private fun demoControlColors(material3: Boolean): Pair<CompassButtonStyle, Color> =
  if (material3) {
    val colors = ButtonDefaults.elevatedButtonColors()
    CompassButtonStyle(
      containerColor = colors.containerColor,
      shadowElevation = 1.dp,
      hoveredShadowElevation = 3.dp,
    ) to colors.contentColor
  } else {
    CompassDefaults.style() to ZoomButtonsDefaults.ContentColor
  }

/** Same chrome as [org.maplibre.compose.overlay.CompassButton]. */
@Composable
private fun DemoControlButton(
  onClick: () -> Unit,
  style: CompassButtonStyle,
  contentDescription: String,
  onClickLabel: String,
  content: @Composable () -> Unit,
) {
  val interactionSource = remember { MutableInteractionSource() }
  val hovered by interactionSource.collectIsHoveredAsState()
  val shadowElevation by
    animateDpAsState(if (hovered) style.hoveredShadowElevation else style.shadowElevation)
  Box(
    Modifier.requiredSize(DemoControlSize)
      .shadow(shadowElevation, style.shape, clip = false)
      .background(style.containerColor, style.shape)
      .clip(style.shape)
      .semantics {
        this.contentDescription = contentDescription
        onClick(label = onClickLabel, action = null)
      }
      .clickable(
        interactionSource = interactionSource,
        indication = LocalIndication.current,
        role = Role.Button,
        onClick = onClick,
      )
      .padding(12.dp),
    contentAlignment = Alignment.Center,
    content = { content() },
  )
}

/**
 * The shared map, the selected demo's overlay, the pointer pin, and the diagnostic overlays.
 *
 * [overlay] holds the map controls; a shell with little room, such as a watch, passes a smaller set
 * than the settings ask for.
 */
@Composable
fun DemoMap(
  state: DemoAppState,
  viewportInsets: MapViewportInsets,
  controls: DemoMapControls = demoMapControls(state.settings, state.location),
  modifier: Modifier = Modifier,
) {
  val scope = rememberCoroutineScope()
  val appliedBase = state.appliedStyle.base
  val selectedDemo = state.selectedDemo
  LaunchedEffect(state.mapState.style.loadState, appliedBase) {
    when (state.mapState.style.loadState) {
      StyleLoadState.Ready,
      is StyleLoadState.Failed -> state.noteStyleLoad(appliedBase)
      else -> Unit
    }
  }
  LaunchedEffect(state.mapState) {
    withContext(Dispatchers.Default) {
      state.mapState.events.collect {
        if (it is MapEvent.FrameRendered) state.frameRateState.record()
      }
    }
  }
  val pointerPin = selectedDemo?.pointerPin
  val baseInteractions = state.settings.interactions
  val baseUiOptions = state.settings.boundUiOptions
  val placementPadding =
    PaddingValues.Absolute(
      left = viewportInsets.left + MapOverlay.Spacing,
      top = viewportInsets.top + MapOverlay.Spacing,
      right = viewportInsets.right + MapOverlay.Spacing,
      bottom = viewportInsets.bottom + MapOverlay.Spacing,
    )
  Box(Modifier.fillMaxSize()) {
    MaplibreMap(
      state = state.mapState,
      modifier = modifier.then(selectedDemo?.mapModifier(state.mapState) ?: Modifier),
      viewportInsets = viewportInsets.asPaddingValues(),
      renderOptions = state.settings.renderOptions,
      interactions =
        if (state.location.placingMockLocation) {
          MapInteractions {
            callbacks {
              click {
                onEvent { event ->
                  state.location.placeMockLocation(event.position)
                  ClickResult.Consume
                }
              }
            }
          }
        } else selectedDemo?.interactions(state.mapState, baseInteractions) ?: baseInteractions,
      uiOptions = selectedDemo?.uiOptions(baseUiOptions) ?: baseUiOptions,
    ) {
      if (selectedDemo == null) DefaultMapControls(controls)
      selectedDemo?.let { demo ->
        key(demo) {
          with(demo) { Overlay(state, controls) }
          GeographicLayout(Modifier.controlPadding()) {
            pointerPin?.let {
              val onClick: () -> Unit = {
                scope.launch {
                  state.mapState.flyTo(it.destination, state.settings.flightAnimation)
                }
              }
              val description = "Fly back to ${demo.name}"
              if (state.settings.useMaterial3Controls) {
                MaterialPointerPinButton(targetPosition = it.target, onClick = onClick) {
                  Icon(vectorResource(Res.drawable.filter_center_focus_24px), description)
                }
              } else {
                PointerPinButton(targetPosition = it.target, onClick = onClick) {
                  Icon(
                    vectorResource(Res.drawable.filter_center_focus_24px),
                    description,
                    tint = ZoomButtonsDefaults.ContentColor,
                  )
                }
              }
            }
          }
        }
      }
    }

    if (state.settings.showPointerPinDiagnostics && pointerPin != null) {
      PointerPinDestinationOverlay(
        mapState = state.mapState,
        destination = pointerPin.destination,
        modifier = Modifier.fillMaxSize(),
      )
      PointerPinPlacementOverlay(
        mapState = state.mapState,
        target = pointerPin.target,
        placementPadding = placementPadding,
        modifier = Modifier.fillMaxSize(),
      )
    }

    Box(Modifier.fillMaxSize().padding(placementPadding)) {
      if (state.location.placingMockLocation) {
        Surface(
          modifier = Modifier.align(Alignment.TopCenter),
          shape = MaterialTheme.shapes.medium,
          tonalElevation = 6.dp,
        ) {
          Row(
            Modifier.padding(start = 16.dp, end = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
          ) {
            Text("Tap map to set mock position", modifier = Modifier.weight(1f))
            TextButton(onClick = state.location::cancelMockPlacement) { Text("Cancel") }
          }
        }
      } else {
        DiagnosticOverlays(state = state, modifier = Modifier.align(Alignment.TopCenter))
      }
    }
  }
}

@Composable
private fun PointerPinDestinationOverlay(
  mapState: MapState,
  destination: DemoDestination,
  modifier: Modifier = Modifier,
) {
  // The viewport read recomputes the projection when the camera changes.
  val viewport = mapState.viewport
  val projected =
    remember(destination, viewport) {
      when (destination) {
        is DemoDestination.ExactCamera ->
          mapState.screenLocationFromPosition(destination.position.center)?.let(::listOf)
        is DemoDestination.FitBounds -> {
          val bounds = destination.bounds
          listOf(
              Position(longitude = bounds.west, latitude = bounds.north),
              Position(longitude = bounds.east, latitude = bounds.north),
              Position(longitude = bounds.east, latitude = bounds.south),
              Position(longitude = bounds.west, latitude = bounds.south),
            )
            .mapNotNull { mapState.screenLocationFromPosition(it) }
            .takeIf { it.size == 4 }
        }
        DemoDestination.None -> null
      }
    }
  val color = Color(0xFFE53935)

  if (projected != null) {
    Canvas(modifier) {
      val points = projected.map { Offset(it.x.toPx(), it.y.toPx()) }
      if (points.size == 1) {
        drawCircle(
          color = color,
          radius = 12.dp.toPx(),
          center = points.single(),
          style = Stroke(width = 3.dp.toPx()),
        )
      } else {
        val path =
          Path().apply {
            moveTo(points.first().x, points.first().y)
            points.drop(1).forEach { lineTo(it.x, it.y) }
            close()
          }
        drawPath(path = path, color = color, style = Stroke(width = 3.dp.toPx()))
      }
    }
  }
}

@Composable
private fun PointerPinPlacementOverlay(
  mapState: MapState,
  target: Position,
  placementPadding: PaddingValues,
  modifier: Modifier = Modifier,
) {
  val viewport = mapState.viewport
  val projected = remember(target, viewport) { mapState.screenLocationFromPosition(target) }
  val layoutDirection = LocalLayoutDirection.current

  Canvas(modifier) {
    val geometryColor = Color(0xFF00ACC1)
    val targetColor = Color(0xFFFFB300)
    val haloColor = Color.White.copy(alpha = 0.9f)
    val geometryWidth = 2.dp.toPx()
    val haloWidth = 5.dp.toPx()
    val area =
      Rect(
        left = placementPadding.calculateLeftPadding(layoutDirection).toPx(),
        top = placementPadding.calculateTopPadding().toPx(),
        right = size.width - placementPadding.calculateRightPadding(layoutDirection).toPx(),
        bottom = size.height - placementPadding.calculateBottomPadding().toPx(),
      )
    if (area.width <= 0 || area.height <= 0) return@Canvas

    drawOval(
      color = haloColor,
      topLeft = area.topLeft,
      size = area.size,
      style = Stroke(width = haloWidth),
    )
    drawOval(
      color = geometryColor,
      topLeft = area.topLeft,
      size = area.size,
      style = Stroke(width = geometryWidth),
    )

    val projectedTarget = projected?.let { Offset(it.x.toPx(), it.y.toPx()) } ?: return@Canvas
    val intersection = findEllipseIntersection(area, projectedTarget)

    if (intersection != null) {
      drawLine(
        color = haloColor,
        start = intersection,
        end = projectedTarget,
        strokeWidth = haloWidth,
      )
      drawLine(
        color = geometryColor,
        start = intersection,
        end = projectedTarget,
        strokeWidth = geometryWidth,
      )
      drawCircle(color = haloColor, radius = 6.dp.toPx(), center = intersection)
      drawCircle(color = geometryColor, radius = 4.dp.toPx(), center = intersection)
    }

    val targetRadius = 9.dp.toPx()
    val targetArm = 13.dp.toPx()
    drawCircle(
      color = haloColor,
      radius = targetRadius,
      center = projectedTarget,
      style = Stroke(width = haloWidth),
    )
    drawCircle(
      color = targetColor,
      radius = targetRadius,
      center = projectedTarget,
      style = Stroke(width = geometryWidth),
    )
    listOf(haloWidth to haloColor, geometryWidth to targetColor).forEach { (width, color) ->
      drawLine(
        color = color,
        start = Offset(projectedTarget.x - targetArm, projectedTarget.y),
        end = Offset(projectedTarget.x + targetArm, projectedTarget.y),
        strokeWidth = width,
      )
      drawLine(
        color = color,
        start = Offset(projectedTarget.x, projectedTarget.y - targetArm),
        end = Offset(projectedTarget.x, projectedTarget.y + targetArm),
        strokeWidth = width,
      )
    }
  }
}

// Matches the placement calculation in PointerPinButton.
private fun findEllipseIntersection(area: Rect, target: Offset): Offset? {
  val delta = target - area.center
  val horizontalRadius = area.width / 2.0
  val verticalRadius = area.height / 2.0
  val normalizedDistance =
    delta.x * delta.x / (horizontalRadius * horizontalRadius) +
      delta.y * delta.y / (verticalRadius * verticalRadius)
  if (normalizedDistance < 1.0) return null
  return area.center + delta * (1.0 / sqrt(normalizedDistance)).toFloat()
}

@Composable
private fun DiagnosticOverlays(state: DemoAppState, modifier: Modifier = Modifier) {
  Column(
    modifier =
      modifier
        .padding(8.dp)
        .background(
          color = MaterialTheme.colorScheme.surface.copy(alpha = 0.8f),
          shape = RoundedCornerShape(8.dp),
        )
        .padding(horizontal = 8.dp),
    horizontalAlignment = Alignment.CenterHorizontally,
  ) {
    if (state.settings.showPointerPinDiagnostics && state.selectedDemo?.pointerPin != null) {
      Text(
        text = "Destination: red · target: amber · placement: cyan",
        style = MaterialTheme.typography.labelMedium,
      )
    }
    if (state.settings.showFpsOverlay) {
      LaunchedEffect(state.frameRateState) { state.frameRateState.track() }
      Text(
        text = "${state.frameRateState.framesPerSecond.roundToInt()} fps",
        style = MaterialTheme.typography.labelMedium,
      )
    }
    if (state.settings.showCameraOverlay) {
      val position = state.mapState.cameraPosition
      Text(
        text =
          "lat ${position.center.latitude.format(4)} " +
            "lng ${position.center.longitude.format(4)} " +
            "zoom ${position.zoom.format(1)} " +
            "bearing ${position.bearing.format(0)} " +
            "pitch ${position.pitch.format(0)}",
        style = MaterialTheme.typography.labelMedium,
      )
    }
  }
}

private fun Double.format(decimals: Int): String {
  var factor = 1.0
  repeat(decimals) { factor *= 10 }
  val rounded = (this * factor).roundToInt() / factor
  return if (decimals == 0) rounded.roundToInt().toString() else rounded.toString()
}

/** The shared controls' normal placement; a demo can instead allocate space for their contents. */
@Composable
internal fun DefaultMapControls(controls: DemoMapControls) {
  Box(Modifier.fillMaxSize().controlPadding()) {
    Box(Modifier.align(Alignment.TopStart)) { controls.scale() }
    Box(Modifier.align(Alignment.TopEnd)) { controls.buttons() }
    Box(Modifier.align(Alignment.BottomCenter).fillMaxWidth()) { controls.attribution() }
  }
}

@Composable
private fun DemoMapAttribution(material3: Boolean) {
  Row(
    Modifier.fillMaxWidth(),
    horizontalArrangement = Arrangement.SpaceBetween,
    verticalAlignment = Alignment.Bottom,
  ) {
    MaplibreLogo()
    if (material3) MaterialExpandingAttributionButton() else ExpandingAttributionButton()
  }
}
